/**
 * styles.css — Valle della Pace tenant-scoped CSS.
 *
 * Injected AFTER src/styles/variables.css by init.js injectSharedStyles(), so these
 * :host(.ws-tenant-valledellapace) rules override the base :host vars and the shared rich-card
 * scaffold. Moved verbatim out of variables.css (split per-winery for maintainability).
 */

/* ════════════════════════════════════════════════════════════════════════
   Valle della Pace — exact parity overrides (live old widget on
   valledellapace.com → valledellapace.concierge.redaugment.com/static/javascript/script.js)
   Valle's real palette differs from the shared Halleck/Hestan magenta: the
   launcher + header are light-green #ECF2D1, prompt buttons are WHITE outlined
   in brown #574D39 (not magenta gradients), brand red is var(--ws-primary). These rules
   come AFTER the shared :is() block, so for valledellapace they win.
   ════════════════════════════════════════════════════════════════════════ */

:host(.ws-tenant-valledellapace) .ws-header {
  background-color: #ecf2d1;
}

/* Launcher rail: black "ASK SOPHIA", Open Sans 18px/400, 0.36px tracking,
   no drop shadow (old .webaskjess: #ECF2D1 bg, black text). */
:host(.ws-tenant-valledellapace) .launcher {
  background-color: #ecf2d1;
  color: #000000;
  box-shadow: none;
}
:host(.ws-tenant-valledellapace) .launcher:hover { box-shadow: none; }
:host(.ws-tenant-valledellapace) .launcher .label {
  color: #000000;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.36px;
  text-transform: none;
}

/* Chat bubbles — match the old live widget exactly. The base widget paints the
   user bubble with --ws-primary (#ECF2D1, Valle's light-green chrome), which left
   white text nearly invisible. Old Valle uses a grey gradient for the user bubble
   and a light-grey gradient + brown text for the bot bubble.
   Reference: feat/valledellapace_c7/static/css/index.css 4639-4651
     .bot-message .message-content  -> linear-gradient(#f7f6f6,#ededed), #574d39, radius 0 8 8 8
     .user-message .message-content -> linear-gradient(#989696,#807c7c), #fff,     radius 6 0 6 6 */
:host(.ws-tenant-valledellapace) .ws-bot-bubble,
:host(.ws-tenant-valledellapace) .ws-welcome {
  color: #000;
  border-radius: 8px;
  border-top-left-radius: 0;
}
:host(.ws-tenant-valledellapace) .ws-user-bubble {
  background: linear-gradient(#989696, #807c7c);
  color: #FFFFFF;
  border-radius: 6px;
  border-top-right-radius: 0;
}

/* Welcome prompt buttons: white pills outlined in brown with a bolt icon
   (old .option-button: #fff bg, #574D39 text+border, Open Sans 15px/600,
   radius 8px, padding 8px 12px, gap 5px). The widget renders these as
   .ws-prompt-chip (not .ws-prompt-button). On click/hover they fill var(--ws-primary)
   white (old changeBKG). */
/* Chip layout (indent + shrink-to-text) is shared in ChatPanel.css .ws-prompts. */
:host(.ws-tenant-valledellapace) .ws-prompt-chip {
  background: #FFFFFF;
  color: #574D39;
  border: 1px solid #574D39;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  padding: 8px 12px;
  gap: 5px;
}
/* Old hover is tan #f4e3c1 with the brown text kept (old .option-button:hover,
   index.css 4159-4164). The maroon var(--ws-primary) was the CLICK state (changeBKG), but
   our chips disappear on click, so hover = tan for parity. */
:host(.ws-tenant-valledellapace) .ws-prompt-chip:hover {
  background: var(--ws-primary);
  color: #fff;
}
/* Bolt icon matches the text color (currentColor #574D39, kept on hover). */
:host(.ws-tenant-valledellapace) .ws-prompt-chip .ws-prompt-icon { color: inherit; }
/* Selected (clicked) state — maroon fill + white, matching the old changeBKG.
   Needed here because Valle's --ws-primary is a pale color, so the base
   .is-selected rule (which fills with --ws-primary) would be unreadable. */
:host(.ws-tenant-valledellapace) .ws-prompt-chip.is-selected {
  background: var(--ws-primary) !important;
  border-color: var(--ws-primary) !important;
  color: #FFFFFF !important;
}
:host(.ws-tenant-valledellapace) .ws-prompt-chip.is-selected .ws-prompt-icon { color: inherit; }

/* Login form (.ws-vp-login) + Commerce7 checkout steps (.ws-vp-checkout) share
   these classes with Lightning Rock Winery, whose base rules now read
   var(--ws-primary). Same reason as the prompt-chip override above — Valle's
   --ws-primary is a pale color, so pin these back to the old maroon. */
:host(.ws-tenant-valledellapace) .ws-vp-input {
  outline-color: var(--ws-primary);
}
:host(.ws-tenant-valledellapace) .ws-vp-login-submit {
  background: var(--ws-primary);
}
:host(.ws-tenant-valledellapace) .ws-vp-login-hint a,
:host(.ws-tenant-valledellapace) .ws-vp-login-skip {
  color: var(--ws-primary);
}

/* Wine listing card (old createWineListing): white card with a soft gold glow,
   red title, red uppercase "Add to Cart". Overrides the shared magenta. */
:host(.ws-tenant-valledellapace) .ws-rich-winecard {
  border-radius: 6px;
  box-shadow: 0 0 10px rgba(189, 133, 39, 0.4);
  /* Equal-height carousel: column card + body flex:1 + footer margin-top:auto so
     the price + ADD TO CART line up across cards even when a title wraps to two
     lines (e.g. "Cabernet Sauvignon, Whiskey-Barrel"). */
  display: flex;
  flex-direction: column;
}

:host(.ws-tenant-valledellapace) .ws-card-title {
  color: var(--ws-primary);
}

:host(.ws-tenant-valledellapace) .ws-rich-body { flex: 1; }
:host(.ws-tenant-valledellapace) .ws-rich-winetitle {
  color: var(--ws-primary);
  font-size: 16px;
  font-weight: 600;
}
:host(.ws-tenant-valledellapace) .ws-card-cta,
:host(.ws-tenant-valledellapace) .ws-rich-cta {
  background: var(--ws-primary);
  color: #FFFFFF;
  border: 1px solid var(--ws-primary);
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
}
:host(.ws-tenant-valledellapace) .ws-card-cta:hover,
:host(.ws-tenant-valledellapace) .ws-rich-cta:hover {
  background: #5E0210;
}
/* Wine card footer: price block (LEFT, price over bottle-size) + a compact
   Add-to-cart (RIGHT) on ONE row — matches old .product-details (flex,
   space-between, align-center) with a content-width button, NOT the shared
   stacked full-width layout. Reference: index.css 2505-2543. */
:host(.ws-tenant-valledellapace) .ws-rich-footer {
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: auto; /* pin price + ADD TO CART to the bottom of the card */
}
:host(.ws-tenant-valledellapace) .ws-rich-pricerow {
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
}
:host(.ws-tenant-valledellapace) .ws-rich-cta,
:host(.ws-tenant-valledellapace) .ws-vp-add {
  display: inline-block;
  width: auto;
  white-space: nowrap; /* keep "ADD TO CART" on one line */
  padding: 9px 10px;
  text-align: center;
}
/* "Tasting notes" link — red to match the card (old tasting-notes-link). */
:host(.ws-tenant-valledellapace) .ws-rich-tasting {
  color: var(--ws-primary);
  border-color: rgba(122, 2, 20, 0.35);
}

/* ── Wine listing card (parity: createWineListing) ────────────────────── */
:host(.ws-tenant-valledellapace) .ws-valle-winecard-head {
  position: relative;
  height: 180px;
  min-height: 180px;
  max-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

:host(.ws-tenant-valledellapace) .ws-valle-winecard-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
}

:host(.ws-tenant-valledellapace) .ws-valle-winecard-soldout {
  position: absolute;
  right: 8px;
  top: 8px;
  text-align: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 3px;
  width: fit-content;
  background-color: #000000ab;
  color: #fff;
}

:host(.ws-tenant-valledellapace) .ws-valle-winecard-title {
  margin: 0 0 4px;
  line-height: normal;
  min-height: 40px;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Old .wine_card_vineyard — hardcoded under title on Shop Our Wines cards. */
:host(.ws-tenant-valledellapace) .ws-valle-winecard-vineyard {
  margin: 0 0 0;
  font-size: 0.75rem;
  color: #000;
}

:host(.ws-tenant-valledellapace) .ws-valle-winecard-meta {
  margin: 0;
  font-size: 15px;
  font-weight: 400;
  line-height: 120%;
  color: #1d1d1d;
}

:host(.ws-tenant-valledellapace) .ws-valle-winecard-body {
  margin-top: 10px;
}

:host(.ws-tenant-valledellapace) .ws-valle-winecard-tasting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 12px;
  margin: auto;
  margin-bottom: 10px;
  border: 1px solid var(--ws-primary);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 4px 9.7px 0 rgba(0, 0, 0, 0.16);
  color: var(--ws-primary);
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}

/* Wine-club "pick your wines" card (shared ClubWineSelection.js) — pin the
   accent to --ws-primary like every other wine-card/tasting override in
   this file (Valle's --ws-primary is the pale header green, not the brand
   maroon var(--ws-card-accent) would otherwise fall back to). */
:host(.ws-tenant-valledellapace) .ws-club-selection-tasting {
  border-color: var(--ws-primary);
  color: var(--ws-primary);
}
:host(.ws-tenant-valledellapace) .ws-club-selection-member-price,
:host(.ws-tenant-valledellapace) .ws-club-selection-total {
  color: var(--ws-primary);
}

:host(.ws-tenant-valledellapace) .ws-valle-winecard-product-details {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  margin-top: auto;
  padding-top: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

/* Footer holds BOTH the price block and the Add to cart / qty-stepper action
   — a row (price left, action right), not stacked, so Add to cart doesn't
   stretch full-width below the price. */
:host(.ws-tenant-valledellapace) .ws-valle-winecard-product-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

:host(.ws-tenant-valledellapace) .ws-valle-winecard-purchase-row {
  display: flex;
  flex-direction: column;
}

:host(.ws-tenant-valledellapace) .ws-valle-price {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ws-primary);
}
:host(.ws-tenant-valledellapace) .ws-valle-winecard-unit {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: #000;
}
:host(.ws-tenant-valledellapace) .ws-valle-add {
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 4px;
  background: var(--ws-primary);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

:host(.ws-tenant-valledellapace) .ws-valle-product-action,
:host(.ws-tenant-valledellapace) .ws-valle-winecard .product-action {
  display: flex;
  align-items: stretch;
  gap: 10px;
  margin-left: auto;
  white-space: nowrap;
  width: auto;
}

:host(.ws-tenant-valledellapace) .ws-cp-qty-wrap,
:host(.ws-tenant-valledellapace) .ws-valle-winecard .product-quantity-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 0;
}

:host(.ws-tenant-valledellapace) .ws-valle-winecard .product-action button {
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  min-width: 30px;
  max-width: 30px;
  height: 30px;
  min-height: 30px;
  padding: 0 !important;
  border-radius: 2px;
  border: 1px solid #e5e5e5;
  background: #f1f1f1;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  color: #0a0a0a;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  text-transform: none;
}

:host(.ws-tenant-valledellapace) .ws-cp-delete svg,
:host(.ws-tenant-valledellapace) .ws-valle-winecard .delete-cart svg {
  width: 16px;
  height: 16px;
  display: block;
}

:host(.ws-tenant-valledellapace) .ws-cp-cart-qty,
:host(.ws-tenant-valledellapace) .ws-valle-winecard .cart-qty {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 30px;
  padding: 2px 10px;
  margin: 0;
  border: none;
  background: transparent;
  box-shadow: none;
  color: #0a0a0a;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  text-align: center;
}

/* "Learn more" button (tasting/event cards) — white pill, red border + text,
   not the shared magenta var(--ws-card-accent). Matches old .learn-more across
   carousels (index.css: border 1px var(--ws-primary), color var(--ws-primary), white bg, radius 20px).
   The icon stroke uses currentColor, so it follows this red automatically. */
:host(.ws-tenant-valledellapace) .ws-card-learnmore {
  border-color: var(--ws-primary);
  color: var(--ws-primary);
}

/* Cart panel (old createCartProducts): red item title, white-outlined action
   buttons (old .order-actions .option-button — both View cart + Proceed). */
:host(.ws-tenant-valledellapace) .ws-cart-item-title { color: var(--ws-primary); font-size: 14px; }
:host(.ws-tenant-valledellapace) .ws-cart-item-meta { color: #000000; }
/* Per-item price: the base uses --ws-card-accent, which for Valle falls back to
   the pale --ws-primary (#ECF2D1) and is unreadable. Use the olive accent
   (accent_color #574D39), matching the live cart. */
:host(.ws-tenant-valledellapace) .ws-cart-item-price { color: var(--ws-accent); }
:host(.ws-tenant-valledellapace) .ws-cart-action-btn {
  background: #FFFFFF;
  color: #574D39;
  border: 1px solid #574D39;
}
:host(.ws-tenant-valledellapace) .ws-cart-action-btn:hover {
  background: var(--ws-primary);
  color: #FFFFFF;
  border-color: var(--ws-primary);
  opacity: 1;
}

/* Membership card (old createWineMembership): WHITE card with a gold glow, red
   title + Join Now, brown body — NOT the burgundy goose card. Overrides the
   shared #691B38 rules. */
:host(.ws-tenant-valledellapace) .ws-rich-membership {
  background: #FFFFFF;
  border-radius: 6px;
  box-shadow: 0 0 10px rgba(189, 133, 39, 0.4);
  /* Same equal-height carousel as events: column card + inner flex:1 + footer
     margin-top:auto so the price + Join Now line up across all club cards
     (e.g. "Flexible Four" has a longer discount line that would otherwise push
     its footer down). */
  display: flex;
  flex-direction: column;
}
:host(.ws-tenant-valledellapace) .ws-rich-membership .ws-card-head { background: transparent; }
/* Fallback winery logo on top when a club card has no cover image (old
   defaultCardLogo = valle-bkg.webp with padding:19px, in a 16/9 area). */
:host(.ws-tenant-valledellapace) .ws-rich-membership .ws-card-img.ws-vp-mem-logo {
  aspect-ratio: 16 / 9;
  object-fit: contain;
  background: #FFFFFF;
}
:host(.ws-tenant-valledellapace) .ws-rich-membership .ws-card-title { color: var(--ws-primary); }
:host(.ws-tenant-valledellapace) .ws-rich-inner {
  background: transparent;
  flex: 1;
  display: flex;
  flex-direction: column;
}
/* Pin the bottom group (Learn more → price + Join Now) to the card bottom so it
   lines up across club cards. Learn more is the first bottom-group element. */
:host(.ws-tenant-valledellapace) .ws-rich-membership .ws-card-learnmore { margin-top: auto; }
:host(.ws-tenant-valledellapace) .ws-rich-check { color: #574D39; }
/* price_additional_text line at top of inner (old .card-box-price: 14px/400). */
:host(.ws-tenant-valledellapace) .ws-vp-mem-discount {
  margin: 0 0 6px 5px;
  font-size: 14px;
  font-weight: 400;
  color: #574D39;
}
/* Footer price block: price (red 16px) over price_unit (old
   .membership-price-details). Footer itself is the shared row (price left,
   Join Now right). */
:host(.ws-tenant-valledellapace) .ws-vp-mem-price { display: flex; flex-direction: column; }
:host(.ws-tenant-valledellapace) .ws-vp-mem-price .ws-card-price {
  margin: 0; font-size: 16px; font-weight: 700; color: var(--ws-primary);
}
:host(.ws-tenant-valledellapace) .ws-vp-mem-price .ws-card-unit {
  margin: 0; font-size: 12px; color: #000000;
}

/* Event/review accents: red instead of the shared magenta/burgundy. (The event
   card's blurred-overlay layout still differs structurally from the old white
   image-on-top card — see PROGRESS follow-up.) */
:host(.ws-tenant-valledellapace) .ws-rich-event-strip {
  background: linear-gradient(90deg, #5E0210 0%, var(--ws-primary) 50%, #5E0210 100%);
}
:host(.ws-tenant-valledellapace) .ws-rich-event-date,
:host(.ws-tenant-valledellapace) .ws-rich-event-price { color: var(--ws-primary); }
:host(.ws-tenant-valledellapace) .ws-rich-reserve-cta { background: var(--ws-primary); }

/* Event card (old createEvents): WHITE card, cover image on top, header block,
   pill "Learn more", price + red "Get tickets" CTA. */
:host(.ws-tenant-valledellapace) .ws-vp-event {
  background: #FFFFFF;
  border-radius: 6px;
  box-shadow: 0 0 10px rgba(189, 133, 39, 0.4);
  overflow: hidden;
  /* Carousel items are equal-height (track is align-items:stretch); make the
     card a column so the footer (Learn more + price + CTA) pins to the bottom
     and lines up across cards regardless of how much header text each has. */
  display: flex;
  flex-direction: column;
}
:host(.ws-tenant-valledellapace) .ws-vp-event-cover img {
  display: block;
  width: 100%;
  height: 168px;
  object-fit: cover;
}
:host(.ws-tenant-valledellapace) .ws-vp-event-inner {
  padding: 10px 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
}
:host(.ws-tenant-valledellapace) .ws-vp-event-title {
  color: var(--ws-primary);
  font-size: 16px;
  font-weight: 400;
  margin: 0 0 4px;
}
:host(.ws-tenant-valledellapace) .ws-vp-event-date {
  color: #000000;
  font-size: 14px;
  font-weight: 700;
  margin: 0;
}
:host(.ws-tenant-valledellapace) .ws-vp-event-time {
  color: #574D39;
  font-size: 13px;
  margin: 2px 0 0;
}
:host(.ws-tenant-valledellapace) .ws-vp-event-location {
  color: #574D39;
  font-size: 14px;
  margin: 2px 0 0;
}
:host(.ws-tenant-valledellapace) .ws-vp-event-desc {
  color: #574D39;
  font-size: 13px;
  margin: 4px 0 0;
}
:host(.ws-tenant-valledellapace) .ws-vp-event-footer {
  margin-top: auto; /* pin to the bottom of the (equal-height) card */
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
:host(.ws-tenant-valledellapace) .ws-vp-event-learn {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--ws-primary);
  background: #FFFFFF;
  border: 1px solid var(--ws-primary);
  border-radius: 20px;
  font-size: 14px;
  padding: 6px 14px;
  cursor: pointer;
}
:host(.ws-tenant-valledellapace) .ws-vp-event-learn:hover { background: #FBF1F5; }
:host(.ws-tenant-valledellapace) .ws-vp-event-pricerow {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
}
:host(.ws-tenant-valledellapace) .ws-vp-event-price p { margin: 0; }
:host(.ws-tenant-valledellapace) .ws-vp-event-price p:first-child { font-weight: 700; color: #000000; }
:host(.ws-tenant-valledellapace) .ws-vp-event-price p:last-child { font-size: 12px; color: #574D39; }
:host(.ws-tenant-valledellapace) .ws-vp-event-cta {
  background: var(--ws-primary);
  color: #FFFFFF;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  padding: 9px 12px;
  text-decoration: none;
}
:host(.ws-tenant-valledellapace) .ws-vp-event-cta:hover { background: #5E0210; }

/* Reviews button row: common look + layout for every tenant now lives in the
   shared cards.css — Valle no longer needs its own override. */

/* Event-details card (old createEventDetails): grey card, decorative top banner,
   white gold-bordered date pill, brown heading, ticket-icon price, bronze
   reservation button. */
:host(.ws-tenant-valledellapace) .ws-vp-event-details {
  background: #F6F6F6;
  border-radius: 8px;
  max-width: 284px;
  padding: 0 0 14px;
  overflow: hidden;
}
:host(.ws-tenant-valledellapace) .ws-vp-ed-top img {
  display: block;
  width: 100%;
}
:host(.ws-tenant-valledellapace) .ws-vp-ed-date {
  display: inline-block;
  margin: 14px 14px 0;
  padding: 4px 10px;
  background: #FFFFFF;
  border: 1px solid rgba(192, 126, 50, 0.19);
  border-radius: 48px;
  font-size: 13px;
  color: #574D39;
}
:host(.ws-tenant-valledellapace) .ws-vp-ed-title {
  margin: 12px 14px 0;
  font-size: 16px;
  font-weight: 700;
  color: #574D39;
}
:host(.ws-tenant-valledellapace) .ws-vp-ed-divider {
  height: 1px;
  margin: 10px 14px;
  background: rgba(0, 0, 0, 0.12);
}
:host(.ws-tenant-valledellapace) .ws-vp-ed-desc {
  margin: 0 14px;
  font-size: 14px;
  line-height: 22px;
  color: #574D39;
}
:host(.ws-tenant-valledellapace) .ws-vp-ed-price {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 14px 0;
}
:host(.ws-tenant-valledellapace) .ws-vp-ed-price p { margin: 0; font-size: 14px; font-weight: 700; color: #574D39; }
:host(.ws-tenant-valledellapace) .ws-vp-ed-amount { font-weight: 500; color: #574D39; }
:host(.ws-tenant-valledellapace) .ws-vp-ed-reserve {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 16px 14px 0;
  padding: 6px 14px;
  background: #AD703E;
  color: #FFFFFF;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer; /* renders as a hrefless <a> until cta_link is configured */
}
:host(.ws-tenant-valledellapace) .ws-vp-ed-reserve:hover { background: #98602F; }

/* ── Header, input bar — Valle-scoped (these are common base components, so
   the overrides are confined to :host(.ws-tenant-valledellapace)) ──────── */

/* Logo: old header-logo.webp renders ~130x50; ours defaulted to 32px tall. */
:host(.ws-tenant-valledellapace) .ws-header-logo img {
  height: 50px;
  max-width: 160px;
}

/* Header icons are dark on the light-green bar (the config's white
   header_action_color makes the currentColor icons — cart/refresh/minimize/
   close — invisible). Override the icon color directly for Valle. */
:host(.ws-tenant-valledellapace) .ws-btn-refresh,
:host(.ws-tenant-valledellapace) .ws-btn-minimize,
:host(.ws-tenant-valledellapace) .ws-btn-close,
:host(.ws-tenant-valledellapace) .ws-btn-cart,
:host(.ws-tenant-valledellapace) .ws-btn-login {
  color: #4B412D;
}
:host(.ws-tenant-valledellapace) .ws-btn-refresh:hover,
:host(.ws-tenant-valledellapace) .ws-btn-minimize:hover,
:host(.ws-tenant-valledellapace) .ws-btn-close:hover,
:host(.ws-tenant-valledellapace) .ws-btn-cart:hover,
:host(.ws-tenant-valledellapace) .ws-btn-login:hover {
  background: rgba(0, 0, 0, 0.06);
}

/* Chat input bar: old input is a light-grey filled pill (rgba(192,195,196,.18),
   radius 6px); ours had a transparent border on white = invisible. The send
   arrow used --ws-primary (#ECF2D1 light-green) = faint; make it the dark accent. */
:host(.ws-tenant-valledellapace) .ws-input-wrap {
  background: rgba(192, 195, 196, 0.18);
  border-color: transparent;
}
:host(.ws-tenant-valledellapace) .ws-btn-send {
  color: #574D39;
}


/* Wine Pagination */

:host(.ws-tenant-valledellapace) .ws-wine-page-button {
    color: var(--ws-primary);
}

:host(.ws-tenant-valledellapace) .ws-wine-page-button.active {
  background-color: var(--ws-primary);
  color: #fff;
}


/* Tasting Notes Drawer . */

:host(.ws-tenant-valledellapace) .ws-drawer-tab-collapse svg path,
:host(.ws-tenant-valledellapace) .ws-wine-drawer-header-icon svg path {
  stroke: var(--ws-primary);
}

:host(.ws-tenant-valledellapace) .ws-wine-drawer-header h2,
:host(.ws-tenant-valledellapace) .ws-wine-drawer-pane h4,
:host(.ws-tenant-valledellapace) .ws-wine-drawer-regular-price,
:host(.ws-tenant-valledellapace) .ws-drawer-fp-heading,
:host(.ws-tenant-valledellapace) .ws-drawer-ingredient-bullet,
:host(.ws-tenant-valledellapace) .ws-drawer-ingredient-amount,
:host(.ws-tenant-valledellapace) .ws-drawer-recipe-back {
  color: var(--ws-primary);
}

:host(.ws-tenant-valledellapace) .ws-wine-drawer-intro,
:host(.ws-tenant-valledellapace) .ws-drawer-pairing-box {
  background: #ffeaed;
}

:host(.ws-tenant-valledellapace) .ws-wine-drawer-add-cart,
:host(.ws-tenant-valledellapace) .ws-wine-drawer-tab.active {
  background: var(--ws-primary);
  color: #fff;
  border-right: 5px solid var(--ws-primary);
}

:host(.ws-tenant-valledellapace) .ws-drawer-fp-card {
  border-top: 3px solid var(--ws-primary);
  background: #ffeaed;
}
:host(.ws-tenant-valledellapace) .ws-drawer-method-num {
  background: var(--ws-primary);
}

